import { css } from '@emotion/react'
import { Button, Cell, Grid, HeadingSection, HFlow, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { ContextualHelpCard } from 'components/ContextualHelpCard'
import {
  ErrorField,
  FormProps,
  FormRenderProps,
  PecFormApi,
  resetAllFieldStates,
  RichtextReference,
  SubmitButton,
} from 'components/form'
import { usePecField } from 'components/form/core/hooks/useField'
import { CboCuidadoCompartilhadoSelectField } from 'components/form/field/select/CboCuidadoCompartilhadoSelectField/CboCuidadoCompartilhadoSelectField'
import { useFlags } from 'config/useFlagsContext'
import { FORM_ERROR } from 'final-form'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { LotacaoContextModel } from 'hooks/atendimento-context/model-atendimentoContext'
import { useMemo, useRef, useState } from 'react'
import { emptyArrayOf } from 'util/array'
import { metaPath } from 'util/metaPath'
import { meta } from 'view/atendimentos/atendimento-individual/model'

import { CuidadoCompartilhadoPlanoModel, TipoCompartilhamentoCuidadoPlanoEnum } from '../model-cuidadocompartilhado'
import { CuidadoCompartilhadoForm } from './CuidadoCompartilhadoForm'
import { CuidadoCompartilhadoFormFields } from './CuidadoCompartilhadoFormFields'
import { CuidadoCompartilhadoFormFieldsPilotoVinculacao } from './CuidadoCompartilhadoFormFieldsPilotoVinculacao'
import { getIsVinculacaoCuidadoAtiva } from './utils-cuidadoCompartilhado'

interface AdicionarCuidadoCompartilhadoFormProps {
  onSubmit: FormProps<CuidadoCompartilhadoPlanoModel>['onSubmit']
  lotacaoAtual: LotacaoContextModel
  isAtendimentoOdonto: boolean
}

const path = metaPath<CuidadoCompartilhadoPlanoModel>()

const emptyInitialValues: CuidadoCompartilhadoPlanoModel = {
  cbo: undefined,
  prioridade: undefined,
  discussao: undefined,
  problemasECondicoes: undefined,
  tipo: undefined,
  unidadeSaude: undefined,
  lastUpdate: undefined,
  atendimentoProfissional: undefined,
  especialidade: undefined,
  _id: undefined,
}

export const AdicionarCuidadoCompartilhadoForm = (props: AdicionarCuidadoCompartilhadoFormProps) => {
  const { onSubmit, lotacaoAtual, isAtendimentoOdonto = false } = props

  const alert = useAlert()
  const richTextFieldRef = useRef<RichtextReference>(null)
  const { analytics } = useAnalytics()
  const flags = useFlags()
  const theme = useTheme()
  const localStyles = createStyles(theme)

  const isVinculacaoCuidadoAtiva = getIsVinculacaoCuidadoAtiva(flags, isAtendimentoOdonto)

  const [disabled, setDisabled] = useState<boolean>(isVinculacaoCuidadoAtiva)

  const {
    acesso: { unidadeSaude },
  } = useAcessoLotacaoOrEstagio()

  const initialValues: CuidadoCompartilhadoPlanoModel = useMemo(
    () =>
      isVinculacaoCuidadoAtiva
        ? emptyInitialValues
        : {
            ...emptyInitialValues,
            tipo: TipoCompartilhamentoCuidadoPlanoEnum.CUIDADO_COMPARTILHADO,
            unidadeSaude: unidadeSaude,
          },
    [isVinculacaoCuidadoAtiva, unidadeSaude]
  )

  const {
    input: { value: cuidadoCompartilhadoAtual },
  } = usePecField({ name: meta.plano.cuidadosCompartilhado.absolutePath(), subscription: { value: true } })

  const encaminhamentos = useMemo(
    () =>
      Array.isArray(cuidadoCompartilhadoAtual)
        ? cuidadoCompartilhadoAtual?.filter(
            (item) => item.tipo === TipoCompartilhamentoCuidadoPlanoEnum.GUIA_ENCAMINHAMENTO
          )
        : emptyArrayOf<CuidadoCompartilhadoPlanoModel>(),
    [cuidadoCompartilhadoAtual]
  )

  const handleSubmit = (values: CuidadoCompartilhadoPlanoModel, form: PecFormApi<CuidadoCompartilhadoPlanoModel>) => {
    onSubmit({ ...values, lotacaoSolicitante: lotacaoAtual }, form)
    if (values.tipo === TipoCompartilhamentoCuidadoPlanoEnum.GUIA_ENCAMINHAMENTO) {
      alert('success', 'Guia de encaminhamento salva com sucesso.')
    } else {
      alert('success', 'Cuidado compartilhado salvo com sucesso.')
    }
    richTextFieldRef.current?.handleReset()
  }

  const onSubmitSucceeded = () => {
    analytics.logEvent('CC_adicionado')
  }

  const renderForm = (renderProps: FormRenderProps<CuidadoCompartilhadoPlanoModel>) => {
    const onCancel = () => {
      renderProps.form.reset()
      resetAllFieldStates(renderProps.form)
      richTextFieldRef.current?.handleReset()
    }

    const titulo = isVinculacaoCuidadoAtiva
      ? 'Encaminhamento/Compartilhamento de cuidado'
      : 'Compartilhamento de cuidado'

    const cboLabel = isVinculacaoCuidadoAtiva ? 'Categoria profissional (CBO)' : 'Compartilhar com a CBO'

    return isVinculacaoCuidadoAtiva ? (
      <VFlow vSpacing={0.5}>
        <ContextualHelpCard
          title='Agora ficou mais fácil: Encaminhamento e Compartilhamento de cuidado em um só lugar'
          description={
            <Text>
              Unificamos as abas de Encaminhamento e Compartilhamento de cuidado. Agora, você pode escolher o tipo de
              solicitação em um só lugar, de forma mais prática.
              <br />
              Os registros feitos antes dessa mudança ainda estão disponíveis no{' '}
              <strong>Histórico anterior à unificação.</strong>
            </Text>
          }
        />
        <HeadingSection level={4} css={localStyles.heading} title={titulo} />
        <div css={localStyles.wrapper} data-testid='AdicionarCuidadoCompartilhadoForm'>
          <div css={localStyles.header}>
            <Grid>
              <Cell size={6}>
                <CboCuidadoCompartilhadoSelectField
                  name={path.cbo}
                  label={cboLabel}
                  required
                  isAtendimentoOdonto={isAtendimentoOdonto}
                />
              </Cell>
            </Grid>
          </div>
          <div css={localStyles.children}>
            <VFlow>
              <ErrorField name={FORM_ERROR} inline />
              <CuidadoCompartilhadoFormFieldsPilotoVinculacao
                richTextFieldRef={richTextFieldRef}
                path={path}
                disabled={disabled}
                setDisabled={setDisabled}
                isAtendimentoOdonto={isAtendimentoOdonto}
              />
              <HFlow justifyContent='flex-end'>
                <Button size='small' onClick={onCancel}>
                  Cancelar
                </Button>
                <SubmitButton kind='primary' size='small' handleSubmit={renderProps.handleSubmit} disabled={disabled}>
                  Adicionar
                </SubmitButton>
              </HFlow>
            </VFlow>
          </div>
        </div>
      </VFlow>
    ) : (
      <Grid>
        <Cell size={12}>
          <CuidadoCompartilhadoFormFields
            path={path}
            richTextFieldRef={richTextFieldRef}
            isAtendimentoOdonto={isAtendimentoOdonto}
          />
        </Cell>
        <Cell size={12}>
          <HFlow justifyContent='flex-end'>
            <Button size='small' onClick={onCancel}>
              Cancelar
            </Button>
            <SubmitButton kind='primary' size='small' handleSubmit={renderProps.handleSubmit}>
              Adicionar
            </SubmitButton>
          </HFlow>
        </Cell>
      </Grid>
    )
  }

  return (
    <CuidadoCompartilhadoForm
      path={path}
      render={renderForm}
      onSubmit={handleSubmit}
      onSubmitSucceeded={onSubmitSucceeded}
      encaminhamentos={encaminhamentos}
      isAtendimentoOdonto={isAtendimentoOdonto}
      initialValues={initialValues}
    />
  )
}

const createStyles = (theme: Theme) => ({
  header: css`
    background-color: ${theme.pallete.gray.c90};
    border-bottom: 1px solid ${theme.pallete.divider};
    padding: 1rem;
  `,
  children: css`
    padding: 1rem;
  `,
  wrapper: css`
    border: 1px solid ${theme.pallete.divider};
  `,
  heading: css`
    margin-bottom: 0;
  `,
})
